import React from 'react';
import { Assignment } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Paper,
  type SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  type Theme,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';
import { commonStyles, dataFormStyles } from '~/modules/guarantees/legacy/styles.ts';
import { guaranteesClientText } from '../../constants.ts';
import { guaranteesClientPageStyles } from '../../styles.ts';
import ApplicationsTableRow from './ApplicationsTableRow.tsx';
import type { IGuaranteesApplicationsTableProps } from './types.ts';

const GuaranteesApplicationsTable: React.FC<IGuaranteesApplicationsTableProps> = ({
  applications,
  isLoading,
  page,
  pageConfiguration,
  rowsPerPage,
  onPageChange,
  onOpenApplication,
  onRowsPerPageChange,
}) => {
  return (
    <Paper elevation={0} sx={dataFormStyles.documentTableSection}>
      <Box sx={dataFormStyles.documentTableToolbar}>
        <Box sx={dataFormStyles.tableToolbarLeft}>
          <Assignment sx={{ color: 'secondary.main' }} />
          <Typography variant="h6" sx={dataFormStyles.documentTableToolbarTitle}>
            {guaranteesClientText.applicationsTitle}
          </Typography>
        </Box>
      </Box>

      <Box sx={commonStyles.loadingWrap}>
        <TableContainer sx={dataFormStyles.documentTableContainer}>
          <Table
            sx={
              [dataFormStyles.documentTable, guaranteesClientPageStyles.applicationsTable] as SxProps<Theme>
            }
          >
            <TableHead sx={dataFormStyles.documentTableHead}>
              <TableRow>
                <TableCell
                  sx={
                    [
                      (theme) => dataFormStyles.documentTableHeadCell(theme, 'name'),
                      guaranteesClientPageStyles.applicationsTableCellSize.applicationNumber,
                    ] as SxProps<Theme>
                  }
                >
                  {guaranteesClientText.applicationsNumberLabel}
                </TableCell>
                <TableCell
                  align="center"
                  sx={
                    [
                      (theme) => dataFormStyles.documentTableHeadCell(theme, 'createdAt'),
                      guaranteesClientPageStyles.applicationsTableCellSize.createdAt,
                    ] as SxProps<Theme>
                  }
                >
                  {guaranteesClientText.applicationsCreatedAtLabel}
                </TableCell>
                <TableCell align="center" sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'status')}>
                  {guaranteesClientText.statusLabel}
                </TableCell>
                <TableCell align="center" sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'actions')}>
                  {guaranteesClientText.actionsLabel}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!applications.length ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={dataFormStyles.documentEmptyCell}>
                    {guaranteesClientText.emptyApplications}
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((application) => (
                  <ApplicationsTableRow
                    key={application.id}
                    application={application}
                    onOpenApplication={onOpenApplication}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {isLoading && (
          <Box sx={commonStyles.loadingMask}>
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>

      <TablePaginationControls
        page={page}
        rowsPerPage={rowsPerPage}
        totalElements={pageConfiguration.totalElements}
        isLoading={isLoading}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Paper>
  );
};

export default GuaranteesApplicationsTable;
